/**
 * SlashMenu - Command palette for inserting blocks
 *
 * Displays when user types "/" and shows available block types:
 * - Headings (H1, H2, H3)
 * - Lists (bullet, numbered)
 * - Code block
 * - Table
 * - Quote
 * - Divider
 *
 * Supports keyboard navigation (Up/Down/Enter/Escape).
 * Filters items based on typed query.
 *
 * This component is rendered by the SlashCommands extension using
 * tippy.js for positioning near the cursor.
 */

import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';

/**
 * SlashMenuItem - Definition of a single command in the menu
 *
 * Each item has:
 * - title: Display name (e.g., "Heading 1")
 * - description: Brief explanation (e.g., "Large section heading")
 * - icon: Visual indicator (text-based for simplicity)
 * - command: Function to execute when selected
 */
export interface SlashMenuItem {
  title: string;
  description: string;
  icon: string;
  // The command receives editor and range from Tiptap
  // - editor: The Tiptap editor instance
  // - range: The text range to replace (includes the "/" trigger)
  command: (props: { editor: any; range: any }) => void;
}

/**
 * SlashMenuProps - Props passed from the SlashCommands extension
 *
 * - items: Filtered list of commands matching the query
 * - command: Callback to execute when an item is selected
 */
export interface SlashMenuProps {
  items: SlashMenuItem[];
  command: (item: SlashMenuItem) => void;
}

/**
 * SlashMenuRef - Methods exposed to the parent via useImperativeHandle
 *
 * The SlashCommands extension calls onKeyDown to handle keyboard navigation
 * while the menu is open.
 */
export interface SlashMenuRef {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean;
}

/**
 * SlashMenu Component
 *
 * Renders a list of commands with keyboard and mouse navigation.
 * Uses forwardRef to expose keyboard handler to parent extension.
 */
export const SlashMenu = forwardRef<SlashMenuRef, SlashMenuProps>(
  ({ items, command }, ref) => {
    // Track which item is currently highlighted
    const [selectedIndex, setSelectedIndex] = useState(0);

    // Reset selection when items change (new filter applied)
    // This ensures we don't have an invalid index if the list shrinks
    useEffect(() => {
      setSelectedIndex(0);
    }, [items]);

    // Expose keyboard handlers to parent (SlashCommands extension)
    // The parent calls ref.current.onKeyDown(event) for each keypress
    useImperativeHandle(ref, () => ({
      onKeyDown: ({ event }) => {
        // Arrow Up - Move selection up, wrap to bottom if at top
        if (event.key === 'ArrowUp') {
          event.preventDefault();
          setSelectedIndex((prev) =>
            prev === 0 ? items.length - 1 : prev - 1
          );
          return true; // Event handled
        }

        // Arrow Down - Move selection down, wrap to top if at bottom
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          setSelectedIndex((prev) =>
            prev === items.length - 1 ? 0 : prev + 1
          );
          return true; // Event handled
        }

        // Enter - Execute the selected command
        if (event.key === 'Enter') {
          event.preventDefault();
          if (items[selectedIndex]) {
            command(items[selectedIndex]);
          }
          return true; // Event handled
        }

        // Escape - Close the menu (handled by parent, we just signal we processed it)
        if (event.key === 'Escape') {
          event.preventDefault();
          return true; // Event handled
        }

        // Other keys - Not handled by us
        return false;
      },
    }));

    // Empty state when no commands match the query
    if (items.length === 0) {
      return (
        <div className="slash-menu">
          <div className="slash-menu-empty">No matching commands</div>
        </div>
      );
    }

    // Render the command list
    return (
      <div className="slash-menu">
        {items.map((item, index) => (
          <button
            key={item.title}
            type="button"
            className={`slash-menu-item ${
              index === selectedIndex ? 'is-selected' : ''
            }`}
            // Click to select
            onClick={() => command(item)}
            // Hover to highlight (follows mouse)
            onMouseEnter={() => setSelectedIndex(index)}
          >
            {/* Icon container - fixed width for alignment */}
            <span className="slash-menu-item-icon">{item.icon}</span>
            {/* Text content - title and description */}
            <div className="slash-menu-item-content">
              <span className="slash-menu-item-title">{item.title}</span>
              <span className="slash-menu-item-description">
                {item.description}
              </span>
            </div>
          </button>
        ))}
      </div>
    );
  }
);

// Display name for React DevTools
SlashMenu.displayName = 'SlashMenu';
